import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('jwtToken');
        const expiresAt = localStorage.getItem('expiresAt');

        if (token && expiresAt && new Date(expiresAt) > new Date()) {
            setIsAuthenticated(true);
            // Fetch user data on app load if token is valid
            fetchUserData();
        } else {
            localStorage.removeItem('jwtToken');
            localStorage.removeItem('expiresAt');
            setIsAuthenticated(false);
            setLoading(false);
        }
    }, []);

    const fetchUserData = async () => {
        try {
            const response = await api.get('/user');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            // If fetching user data fails, clear token and log out
            localStorage.removeItem('jwtToken');
            localStorage.removeItem('expiresAt');
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = (token, expiresAt) => {
        localStorage.setItem('jwtToken', token);
        localStorage.setItem('expiresAt', expiresAt);
        setIsAuthenticated(true);
        fetchUserData(); // Fetch user data immediately after login
    };

    const logout = useCallback(() => {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('expiresAt');
        setIsAuthenticated(false);
        setUser(null);
        navigate('/login', { replace: true });
    }, [navigate]);

    const hasRole = (role) => {
        return user && user.role === role;
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout, hasRole, fetchUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};